import { useInfiniteQuery, UseInfiniteQueryResult, InfiniteData } from "@tanstack/react-query";

import { useAuth } from "@/dashboard/contexts/auth-context";
import { CursorPage } from "@/dashboard/lib/query/pagination";

type TenantKey<K extends readonly unknown[]> = readonly ["tenant", string, ...K];

export function useInfiniteCursorQuery<
  TItem,
  K extends readonly unknown[] = readonly unknown[],
>(opts: {
  queryKey: K;
  fetchPage: (cursor: string | null, signal?: AbortSignal) => Promise<CursorPage<TItem>>;
  tenantScoped?: boolean;
  enabled?: boolean;
  staleTime?: number;
  refetchInterval?: number | false;
  retry?: number | boolean | ((failureCount: number, error: Error) => boolean);
  retryDelay?: number;
  refetchOnWindowFocus?: boolean | "always";
  refetchOnReconnect?: boolean | "always";
  refetchOnMount?: boolean | "always";
}): UseInfiniteQueryResult<InfiniteData<CursorPage<TItem>>, Error> {
  const {
    queryKey,
    fetchPage,
    tenantScoped = false,
    enabled = true,
    staleTime = 60_000,
    refetchInterval = false,
    retry,
    retryDelay,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchOnMount,
  } = opts;

  const { activeTenantId } = useAuth();
  const fullKey = tenantScoped
    ? (["tenant", activeTenantId, ...queryKey] as TenantKey<K>)
    : queryKey;

  const finallyEnabled = tenantScoped ? !!activeTenantId && enabled : enabled;

  return useInfiniteQuery<CursorPage<TItem>, Error>({
    queryKey: fullKey,
    queryFn: ({ pageParam, signal }) => fetchPage((pageParam as string | null) ?? null, signal),
    initialPageParam: null,
    getNextPageParam: last => last.next_cursor ?? undefined,
    staleTime,
    refetchInterval,
    retry,
    retryDelay,
    refetchOnWindowFocus,
    refetchOnReconnect,
    refetchOnMount,
    enabled: finallyEnabled,
  });
}
